import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import memoize from "@RHCommerceDev/utils/memoize";
import {
  useUserSessionLazyQuery,
  useGetCartProjectLazyQuery
} from "@RHCommerceDev/hooks/queries";
import { updateUserForSessionMutation } from "@RHCommerceDev/graphql-client/queries/session";
import useErrorDialog from "@RHCommerceDev/hooks/useErrorDialog";
import { useMutation } from "@apollo/client";
import { useKeycloak } from "@RHCommerceDev/utils/Keycloak/KeyCloak";
import {
  useCartProjectionAtomValue,
  useUserSessionAtomValue,
  useUserSessionSetAtom
} from "@RHCommerceDev/hooks/atoms";
import yn from "yn";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import isEmpty from "lodash.isempty";
import useCheckEmailPopUp from "@RHCommerceDev/hooks/useCheckEmailPopUp/useCheckEmailPopUp";
import {
  COOKIE_WITH_SESSION,
  USER_TYPES
} from "@RHCommerceDev/utils/constants";
import getCountryFromUrl from "@RHCommerceDev/utils/getCountryFromUrl";
import { useIsoCookies } from "@RHCommerceDev/hooks/useIsoCookies";
import { isValidPostalCode as getIsValidPostalCode } from "@RHCommerceDev/utils/postalcode-validation";
import { countries } from "resources/countries-config.json";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import { CountryCode } from "@RHCommerceDev/hooks/useCountry";
import analyticsLoader from "analytics/loader";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import useLocale from "@RHCommerceDev/hooks/useLocale/useLocale";
import { useRhUserAtomValue } from "@RHCommerceDev/hooks/atoms";
import { useCookiesWithPermission } from "@RHCommerceDev/hooks/useCookiesWithPermission";
import { sleep } from "@RHCommerceDev/utils/sleep";
import {
  useCurrentCartIdValue,
  useSetCurrentCartId
} from "@RHCommerceDev/hooks-atoms/useCurrentCartId";
import { getCookie } from "@RHCommerceDev/utils/cookies";
import { useLocation } from "react-router-dom";

const USER_TYPE_EMAIL = {
  TRADE: "Trade Email",
  CONTRACT: "Contract Email",
  REGISTERED: "Email"
};

const SessionProviderV2 = ({ children }) => {
  const { showError } = useErrorDialog();
  const {
    FEATURE_FASTER_CHECKOUT_SIGN_IN,
    PUBLIC_URL,
    FEATURE_PERFORMANCE_SESSION_MEMBERSHIP,
    FEATURE_CART_ID_ATOM
  } = useEnv();
  const { keycloak } = useKeycloak();
  const setUserSession = useUserSessionSetAtom();
  const rhUser = useRhUserAtomValue();
  const language = useLocale();
  const [called, setCalled] = useState(false);
  const location = useLocation();
  const isUserSessionAPICalled = useRef(false);
  const [isEventTrigger, setIsEventTrigger] = useState(
    !processEnvServer ? !!localStorage.getItem("isLoggedin") : undefined
  );
  const { setStorageValueWrapper } = useCookiesWithPermission();
  const { id: cartProjectionCartId = "" } = useCartProjectionAtomValue();
  const isGuesthouse = PUBLIC_URL?.match(
    /^(?:http(?:s)?:\/\/)?(?:[^\.]+\.)?rhguesthouse/
  );
  const { setSessionCookie, emailPopupData } = useCheckEmailPopUp();
  const { isCartMerge, loading: sessionLoading } = useUserSessionAtomValue();
  const currentCartId = useCurrentCartIdValue();

  const callCartProjectAPI = Boolean(
    yn(FEATURE_CART_ID_ATOM) &&
      isEmpty(cartProjectionCartId) &&
      isEmpty(currentCartId)
  );

  const isCheckout = useMemo(() => {
    try {
      return window?.location?.pathname?.includes("/checkout");
    } catch (error) {
      return false;
    }
  }, []);

  const UNKNOWNpreventer = useCallback((text = "") => {
    try {
      return text?.includes("UNKNOWN") || text?.includes("undefined")
        ? undefined
        : text;
    } catch (error) {
      return undefined;
    }
  }, []);

  const country = getCountryFromUrl();
  const pc = useIsoCookies(["pc"], true)?.pc;
  const cookieCountry = useIsoCookies(["country_cookie"], true)?.country_cookie;
  const { handleSaveCookies } = useUserPreferences();
  const setCurrentCartId = useSetCurrentCartId();
  const sessionId = getCookie("SESSIONID");

  const isValidPostalCode = useMemo(
    () => getIsValidPostalCode(pc, country as CountryCode),
    [country, pc]
  );

  const postalCode = useMemo(
    () =>
      isValidPostalCode && cookieCountry === country
        ? pc
        : countries[country]?.defaultValues?.postalCode,
    [country, isValidPostalCode, pc, cookieCountry]
  );

  const runOnce = useRef(false);
  const handleSaveCookiesRunOnce = useCallback(
    (userSession: SessionUserType) => {
      if (runOnce?.current) {
        return;
      }
      // NO SPECIFIC REGEX FOR DE,BE,ES,FR AND US, ALL CAN HAVE 5 DIGITS
      // if (!isValidPostalCode) {
      handleSaveCookies(
        { country, postalCode: postalCode, language },
        userSession,
        true,
        false
      );
      // }
      runOnce.current = true;
    },
    [country, handleSaveCookies, isValidPostalCode, pc]
  );

  const isCheckoutAndFeatureFasterCheckoutSignIn =
    yn(FEATURE_FASTER_CHECKOUT_SIGN_IN) && isCheckout;

  const [updateUserForSessionQuery] = useMutation<Mutation>(
    updateUserForSessionMutation,
    {
      context: {
        fetchOptions: {
          method: "POST"
        }
      },
      onCompleted: ({ updateUserForSession }) => {
        setUserSession(prev => ({
          ...prev,
          ...updateUserForSession,
          loading: false
        }));
        if (
          isEmpty(emailPopupData) ||
          emailPopupData?.sessionId !== updateUserForSession?.sessionId
        ) {
          setSessionCookie?.(COOKIE_WITH_SESSION, {
            sessionId: updateUserForSession?.sessionId,
            isPopClosed: false,
            clickCount: 1
          });
        }
        if (updateUserForSession?.currentCartId)
          setCurrentCartId(updateUserForSession?.currentCartId);
      },
      onError: showError
    }
  );

  const anonymousCartId = useMemo(
    () => cartProjectionCartId || currentCartId,
    [cartProjectionCartId, currentCartId]
  );

  const isNewlyRegister = useMemo(() => {
    if (USER_TYPES.includes(rhUser?.userType || "")) {
      return !currentCartId ? true : false;
    }
  }, [rhUser?.userType, currentCartId]);

  useEffect(() => {
    async function updateUserSession() {
      if (
        FEATURE_CART_ID_ATOM &&
        !isGuesthouse &&
        !isCheckoutAndFeatureFasterCheckoutSignIn &&
        !isEmpty(keycloak?.tokenParsed) &&
        keycloak?.authenticated &&
        (anonymousCartId || isNewlyRegister) &&
        !called &&
        !isCartMerge &&
        sessionId
      ) {
        setUserSession(prev => {
          return {
            ...prev,
            loadingUpdateUserSession: true,
            isCartMerge: true
          };
        });
        await updateUserForSessionQuery({
          variables: {
            currentCartId: anonymousCartId || "",
            useCartBroker: true,
            userInput: {
              firstName: UNKNOWNpreventer(keycloak?.tokenParsed?.given_name),
              lastName: UNKNOWNpreventer(keycloak?.tokenParsed?.family_name),
              email: keycloak?.tokenParsed?.email,
              platform: "NEW",
              newUser: false
            },
            skipMembershipInfo: yn(FEATURE_PERFORMANCE_SESSION_MEMBERSHIP)
          }
        });
        setUserSession(prev => ({
          ...prev,
          loadingUpdateUserSession: false
        }));
        if (keycloak?.tokenParsed?.email) {
          setCalled(true);
        }
      }
    }
    updateUserSession();
  }, [
    FEATURE_CART_ID_ATOM,
    FEATURE_PERFORMANCE_SESSION_MEMBERSHIP,
    UNKNOWNpreventer,
    called,
    anonymousCartId,
    isNewlyRegister,
    isCheckoutAndFeatureFasterCheckoutSignIn,
    isGuesthouse,
    keycloak?.authenticated,
    keycloak?.tokenParsed,
    sessionLoading,
    sessionId
  ]);

  const [getCartProjection] = useGetCartProjectLazyQuery();
  const [getUserSession] = useUserSessionLazyQuery({
    async onCompleted({ getUserForSession }) {
      setUserSession(prev => ({
        ...prev,
        isMyAccountIconDisabled: false
      }));
      if (
        isEmpty(emailPopupData) ||
        emailPopupData?.sessionId !== getUserForSession?.sessionId
      ) {
        setSessionCookie?.(COOKIE_WITH_SESSION, {
          sessionId: getUserForSession?.sessionId,
          isPopClosed: false,
          clickCount: 1
        });
      }

      if (
        !FEATURE_CART_ID_ATOM &&
        !isGuesthouse &&
        !isCheckoutAndFeatureFasterCheckoutSignIn &&
        !isEmpty(keycloak?.tokenParsed) &&
        getUserForSession?.email !== keycloak?.tokenParsed?.email &&
        USER_TYPES.includes(
          getUserForSession?.rhUser?.userType?.toUpperCase()
        ) &&
        !called
      ) {
        let anonymousCartId = "";
        setUserSession(prev => {
          anonymousCartId = prev?.anonCartId || "";
          return {
            ...prev,
            ...getUserForSession,
            loading: true,
            loadingUpdateUserSession: true
          };
        });
        const { data } = await updateUserForSessionQuery({
          variables: {
            currentCartId:
              anonymousCartId || getUserForSession?.currentCartId || "",

            useCartBroker: true,
            userInput: {
              firstName: UNKNOWNpreventer(keycloak?.tokenParsed?.given_name),
              lastName: UNKNOWNpreventer(keycloak?.tokenParsed?.family_name),
              email: keycloak?.tokenParsed?.email,
              platform: "NEW",
              userType: getUserForSession?.rhUser?.userType,
              newUser: false
            },
            skipMembershipInfo: yn(FEATURE_PERFORMANCE_SESSION_MEMBERSHIP)
          }
        });
        getUserForSession = data?.updateUserForSession || getUserForSession;
        if (keycloak?.tokenParsed?.email) {
          setCalled(true);
        }
      }
      if (getUserForSession?.currentCartId) {
        getCartProjection({
          id: getUserForSession?.rhUser?.id
        });
      }
      setCurrentCartId(getUserForSession?.currentCartId);
      setUserSession(prev => {
        const previousCall = Math.max(0, Number(prev?.prevLoadingCall) || 0);

        return {
          ...prev,
          ...getUserForSession,
          loading: previousCall === 0 ? false : true,
          loadingUpdateUserSession: FEATURE_CART_ID_ATOM
            ? prev?.loadingUpdateUserSession
            : false
        };
      });

      if (!isCheckout) {
        handleSaveCookiesRunOnce(getUserForSession);
      }
    }
  });

  const handleCallGetUserSession = () => {
    isUserSessionAPICalled.current = true;
    setUserSession(prev => ({
      ...prev,
      isMyAccountIconDisabled: true
    }));
    getUserSession();
  };

  useEffect(() => {
    /* use effect and check if there is any wistia hero on the page to delay the getuser and getCartProhjection until the video has been loaded */
    if (window._rh_wait_for_wistia_hero) {
      window._rh_on_wistia_hero_play = window._rh_on_wistia_hero_play || [];
      window._rh_on_wistia_hero_play.push(handleCallGetUserSession);
    } else {
      if (callCartProjectAPI) {
        getCartProjection();
      }
      handleCallGetUserSession();
    }
  }, [keycloak?.tokenParsed]);

  const authenticatedUser = useMemo(() => {
    return USER_TYPES.includes(rhUser?.userType?.toUpperCase() || "");
  }, [rhUser?.userType]);

  useEffect(() => {
    async function triggerEvent() {
      await sleep(500);
      if (
        !processEnvServer &&
        (rhUser?.userType === USER_TYPE_EMAIL.TRADE ||
          rhUser?.userType === USER_TYPE_EMAIL.CONTRACT) &&
        keycloak.authenticated &&
        !isEventTrigger &&
        !rhUser?.loading &&
        authenticatedUser
      ) {
        if (rhUser?.newUser) {
          analyticsLoader(a =>
            a.emitAnalyticsEvent(
              document.querySelector("#spa-root > *")! as HTMLElement,
              a.EVENTS.REGISTER.INT_TYPE,
              {
                userId: rhUser?.id,
                loggedIn: "Success",
                signupStatus: "Success",
                signup_method:
                  USER_TYPE_EMAIL[
                    rhUser?.userType?.toUpperCase() || "REGISTERED"
                  ]
              }
            )
          );
        } else {
          analyticsLoader(a =>
            a.emitAnalyticsEvent(
              document.querySelector("#spa-root > *")! as HTMLElement,
              a.EVENTS.LOGIN.INT_TYPE,
              {
                userId: rhUser?.id,
                loggedIn: "Success",
                login_method:
                  USER_TYPE_EMAIL[
                    rhUser?.userType?.toUpperCase() || "REGISTERED"
                  ]
              }
            )
          );
        }
        setIsEventTrigger(true);
        setStorageValueWrapper({
          storageKey: "isLoggedin",
          value: true
        });
      }
    }
    triggerEvent();
  }, [
    keycloak?.authenticated,
    rhUser?.userType,
    isEventTrigger,
    authenticatedUser,
    rhUser?.loading
  ]);

  useLayoutEffect(() => {
    if (!processEnvServer) {
      let hasWistia = !!document.getElementById("wistia_script");
      if (
        isEmpty(rhUser?.id) &&
        isEmpty(rhUser?.email) &&
        !isUserSessionAPICalled.current &&
        hasWistia
      ) {
        const checkAndCallGetUserSession = () => {
          if (
            !isEmpty(rhUser?.id) ||
            !isEmpty(rhUser?.email) ||
            isUserSessionAPICalled.current
          ) {
            observer.disconnect();
            return;
          }

          const wistiaExists = !!document.getElementById("wistia_script");

          if (hasWistia && !wistiaExists) {
            handleCallGetUserSession();
            observer.disconnect();
          }
        };

        const observer = new MutationObserver(checkAndCallGetUserSession);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
      }
    }
  }, [location?.pathname]);

  return (
    <React.Fragment>
      {children}
    </React.Fragment>
  );
};

export default memoize(SessionProviderV2);
